import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Code, FileText, Fingerprint, Hash, Smartphone, Sparkles, Monitor, Server, User as UserIcon, UserCog, MessageSquareText } from 'lucide-react';
import { Modal, Button, cn } from '../../ui';
import { RoleBadge } from '../../components/RoleBadge';
import { formatIst } from '../../lib/time';
import { MODULE_BADGE, actionBadge, diffChangedFields, redactSensitive, humanLabel, logHref } from './activityConstants';
import { LogValue } from './logValueView';

// Housekeeping columns that are just noise in an audit view — never surface them as a "change" or a
// snapshot field (matched case/underscore-insensitively so createdAt & created_at both drop out).
const NOISE_KEYS = new Set(['id', 'uuid', 'createdat', 'updatedat', 'deletedat', 'createdby', 'updatedby', 'version']);
function stripNoise(v: any): any {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return v;
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(v)) {
    if (!NOISE_KEYS.has(k.toLowerCase().replace(/_/g, ''))) out[k] = val;
  }
  return out;
}

export interface ActorRef { id: string; name: string; role: string; phone?: string | null }
export interface ActivityRow {
  id: string;
  module: string;
  action: string;
  referenceId: string | null;
  referenceLabel?: string | null;
  linkModule?: string | null;
  linkId?: string | null;
  oldValue: any;
  newValue: any;
  message: string | null;
  performedBy: string | null;
  actingOnBehalfOf: string | null;
  source: string | null;
  ipAddress: string | null;
  createdAt: string;
  actor: ActorRef | null;
  onBehalfOf: ActorRef | null;
}

// Parse `<sentence> (field_a, field_b)` → { prefix, fields[] } so the trailing
// changed-field list renders as readable chips instead of snake_case noise.
function parseTrailingFieldList(message?: string | null): { prefix: string; fields: string[] } {
  if (!message) return { prefix: '', fields: [] };
  const m = message.match(/^(.*?)\s*\(([\w-]+(?:\s*,\s*[\w-]+)+)\)\s*$/);
  if (!m) return { prefix: message, fields: [] };
  const fields = m[2].split(',').map((s) => s.trim()).filter(Boolean);
  if (fields.length < 2) return { prefix: message, fields: [] };
  return { prefix: m[1].trim(), fields };
}

function softenEnum(s: unknown): string {
  if (typeof s !== 'string') return String(s);
  if (!/^[A-Z][A-Z0-9_]+$/.test(s)) return s;
  return s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}
function formatScalar(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = String(v).trim();
  if (/^-?\d+\.\d+$/.test(s)) { const n = Number(s); if (Number.isFinite(n)) return n.toString(); }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(s)) return formatIst(s);
  return s;
}

/** "10m ago" — the age of the entry, which is what a reader checks first on an audit trail. */
function relTime(iso: string): string {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30); if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

/**
 * What KIND of thing the entry records, so the dialog can be coloured by what happened rather than
 * arriving in the same grey for a deletion and a login. The action vocabulary is open-ended (every
 * module coins its own verbs), so these are patterns rather than a key list — an unrecognised verb
 * lands on `edit`, which is the honest default for "something was written".
 */
type Kind = 'create' | 'destroy' | 'flow' | 'shift' | 'io' | 'edit';
const KIND_RULES: Array<[RegExp, Kind]> = [
  [/DELET|REMOV|CANCEL|REJECT|DEACTIVAT|UNASSIGN|FAIL|PASSWORD|VOID/, 'destroy'],
  [/CREAT|APPROV|ACTIVAT|DELIVER|PAYMENT|CONVERT|TOPPED|RESTOR|LOGIN|CHECKED|VERIFI/, 'create'],
  [/IMPORT|EXPORT|UPLOAD|GENERAT/, 'io'],
  [/SUBMIT|ASSIGN|CLAIM|DISPATCH|SENT|CONTACT/, 'flow'],
  [/STATUS|ADJUST|TRANSFER|REOPEN|REGULARIZ|RETURN|REFUND|DRAFT|ARCHIV/, 'shift'],
];
const kindOf = (action: string): Kind => KIND_RULES.find(([re]) => re.test(action))?.[1] ?? 'edit';

/** The band's tint per kind. Light gradient into the surface so the dialog body reads as one sheet. */
const BAND: Record<Kind, string> = {
  create: 'border-emerald-200 from-emerald-50 dark:border-emerald-900/40 dark:from-emerald-900/20',
  destroy: 'border-rose-200 from-rose-50 dark:border-rose-900/40 dark:from-rose-900/20',
  shift: 'border-amber-200 from-amber-50 dark:border-amber-900/40 dark:from-amber-900/20',
  flow: 'border-cyan-200 from-cyan-50 dark:border-cyan-900/40 dark:from-cyan-900/20',
  io: 'border-purple-200 from-purple-50 dark:border-purple-900/40 dark:from-purple-900/20',
  edit: 'border-blue-200 from-blue-50 dark:border-blue-900/40 dark:from-blue-900/20',
};
/** The matching hue for the band's small caption icons. */
const BAND_ICON: Record<Kind, string> = {
  create: 'text-emerald-600 dark:text-emerald-400',
  destroy: 'text-rose-600 dark:text-rose-400',
  shift: 'text-amber-600 dark:text-amber-400',
  flow: 'text-cyan-600 dark:text-cyan-400',
  io: 'text-purple-600 dark:text-purple-400',
  edit: 'text-blue-600 dark:text-blue-400',
};

/** WEB / MOBILE / SYSTEM → the icon + words a reader recognises. */
function originOf(source: string | null): { icon: ReactNode; label: string } {
  const s = (source || '').toUpperCase();
  if (/MOBILE|ANDROID|IOS|APP/.test(s)) return { icon: <Smartphone className="size-3.5" />, label: 'Mobile app' };
  if (/WEB|BROWSER/.test(s)) return { icon: <Monitor className="size-3.5" />, label: 'Web app' };
  if (!s) return { icon: <Server className="size-3.5" />, label: 'System' };
  return { icon: <Server className="size-3.5" />, label: softenEnum(s) };
}

function ObjectCard({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data || {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!entries.length) return <span className="italic text-muted">—</span>;
  return (
    <div className="space-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2 text-2xs leading-tight">
          <span className="min-w-[80px] text-muted">{humanLabel(k)}</span>
          <span className="break-words text-content">
            {v && typeof v === 'object' ? <ObjectCard data={v as Record<string, unknown>} /> : softenEnum(formatScalar(v))}
          </span>
        </div>
      ))}
    </div>
  );
}

function renderCell(value: unknown): ReactNode {
  if (value === undefined || value === null || value === '') return <span className="italic text-muted">—</span>;
  if (typeof value === 'boolean') return value ? <span className="font-medium text-emerald-600">Yes</span> : <span className="font-medium text-rose-600">No</span>;
  if (Array.isArray(value)) {
    if (!value.length) return <span className="italic text-muted">empty</span>;
    if (value.every((v) => v && typeof v === 'object')) {
      return <div className="space-y-1.5">{value.map((it, i) => <div key={i} className="rounded-control border border-line bg-surface px-2 py-1.5"><ObjectCard data={it as Record<string, unknown>} /></div>)}</div>;
    }
    return <div className="flex flex-wrap gap-1">{value.map((v, i) => <span key={i} className="inline-block rounded bg-surface-2 px-1.5 py-0.5 text-2xs text-content">{softenEnum(formatScalar(v))}</span>)}</div>;
  }
  if (typeof value === 'object') return <ObjectCard data={value as Record<string, unknown>} />;
  return <span className="break-words text-content">{formatScalar(value)}</span>;
}

/** One labelled cell of the identity band. */
function BandCell({ label, icon, children, className }: { label: string; icon?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="flex items-center gap-1.5 type-overline text-subtle">{icon}{label}</div>
      <div className="mt-1 min-w-0 type-body text-content">{children}</div>
    </div>
  );
}

/** A titled block of the body, so every part of the entry is announced the same way. */
function Section({ title, icon, count, children, className, titleClassName }: {
  title: string; icon?: ReactNode; count?: number; children: ReactNode; className?: string; titleClassName?: string;
}) {
  return (
    <section className={cn('mt-4 rounded-card border p-3.5', className ?? 'border-line bg-surface-2/30')}>
      <h3 className={cn('mb-2.5 inline-flex items-center gap-1.5 type-overline', titleClassName ?? 'text-muted')}>
        {icon}{title}{count != null && ` (${count})`}
      </h3>
      {children}
    </section>
  );
}

/**
 * One activity-log entry, in full.
 *
 * This was a right-hand drawer of stacked label/value pairs: it re-stated the module and action four
 * times over, buried the two things a reader is actually after (WHO did it and WHAT changed) below
 * three identical grey cards, and never showed the origin or IP the row already carried. A centered
 * dialog gives the before→after table room to be read across, the band up top answers who/what/where
 * in one line each, and the colour comes from the ACTION — a deletion should not arrive in the same
 * grey as a login.
 */
export function ActivityLogDetailModal({ log, onClose }: { log: ActivityRow; onClose: () => void }) {
  const navigate = useNavigate();
  const [showRaw, setShowRaw] = useState(false);
  const oldRaw = redactSensitive(log.oldValue);
  const newRaw = redactSensitive(log.newValue);
  // The diff + snapshot show only MEANINGFUL fields (housekeeping columns stripped); the "full snapshot"
  // below keeps everything for power users.
  const oldData = stripNoise(oldRaw);
  const newData = stripNoise(newRaw);
  const changed = diffChangedFields(oldData, newData);
  const oldObj = oldData && typeof oldData === 'object' && !Array.isArray(oldData) ? (oldData as Record<string, unknown>) : null;
  const newObj = newData && typeof newData === 'object' && !Array.isArray(newData) ? (newData as Record<string, unknown>) : null;
  const nonEmpty = (o: Record<string, unknown> | null) => !!o && Object.values(o).some((v) => v !== null && v !== undefined && v !== '');
  const hasOld = nonEmpty(oldObj);
  const hasNew = nonEmpty(newObj);
  // A true before→after only when BOTH sides exist; otherwise render a labeled one-sided snapshot so a
  // create / delete / import job doesn't show confusing "— → x" or "x → —" rows.
  const diffMode = hasOld && hasNew && changed.length > 0;
  const isDelete = /DELETE|REMOV/i.test(log.action);
  const snapshotObj = diffMode ? null : hasNew ? newObj : hasOld ? oldObj : null;
  const snapshotEntries = Object.entries(snapshotObj || {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
  const snapshotTitle = isDelete ? 'Removed record'
    : /IMPORT/i.test(log.action) ? 'Import summary'
    : /CREATE|REGISTER|ADD/i.test(log.action) ? 'Created'
    : 'Details';
  // logHref, not entityHref: a shipment row's own reference is the shipment, but the record a reader
  // wants is the ORDER it belongs to — the server says so via linkModule/linkId.
  const href = logHref(log);
  const actor = log.actor;
  const { prefix, fields } = parseTrailingFieldList(log.message);
  const kind = kindOf(log.action);
  const origin = originOf(log.source);

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={880}
      title={<span className="block truncate">{prefix || `${humanLabel(log.module)} ${softenEnum(log.action).toLowerCase()}`}</span>}
      description={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={cn('inline-block rounded px-2 py-0.5 type-caption', MODULE_BADGE[log.module] ?? 'bg-surface-2 text-content')}>{log.module}</span>
          <span className={cn('inline-block rounded px-2 py-0.5 type-caption', actionBadge(log.action))}>{log.action.replace(/_/g, ' ')}</span>
          <span className="text-muted">{formatIst(log.createdAt)}</span>
          <span className="text-subtle">· {relTime(log.createdAt)}</span>
          <span className="inline-flex items-center gap-0.5 text-subtle"><Hash className="size-3" />{log.id}</span>
        </span>
      }
      footer={
        <>
          {href && (
            <Button variant="primary" className="mr-auto" icon={<FileText size={16} />}
              onClick={() => { onClose(); navigate(href); }}>
              Open {humanLabel(log.linkModule ?? log.module).toLowerCase()}
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {/* ── Identity band: who acted, what they touched, and where the action came in from. Three
             columns rather than three stacked cards — these are one sentence, not three topics. ── */}
      <div className={cn('grid gap-3 rounded-card border bg-gradient-to-br to-surface p-3.5 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-line', BAND[kind])}>
        <BandCell label="Who" icon={<UserIcon className={cn('size-3', BAND_ICON[kind])} />} className="sm:pr-4">
          {actor ? (
            <>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="truncate type-body-strong">{actor.name || `#${actor.id}`}</span>
                <RoleBadge role={actor.role} />
              </div>
              {actor.phone && (
                <a href={`tel:${actor.phone}`} className="mt-0.5 block truncate font-mono type-caption text-brand-700 hover:underline dark:text-brand-400">{actor.phone}</a>
              )}
            </>
          ) : (
            <span className="italic text-muted">No actor recorded — written by the system</span>
          )}
          {log.onBehalfOf && (
            // ON_BEHALF is the one path where the name on the row is not the person who pressed the
            // button, so it is called out here rather than left to the payload.
            <div className="mt-1.5 inline-flex flex-wrap items-center gap-1 rounded-control bg-plum-500/10 px-2 py-1 type-caption text-plum-600 dark:text-plum-400">
              <UserCog className="size-3" /> on behalf of {log.onBehalfOf.name}
            </div>
          )}
        </BandCell>

        <BandCell label="Record" icon={<FileText className={cn('size-3', BAND_ICON[kind])} />} className="sm:px-4">
          {log.referenceLabel ? (
            <span className="block break-all font-mono type-body-strong">{log.referenceLabel}</span>
          ) : log.referenceId ? (
            <span className="block font-mono text-muted">#{log.referenceId}</span>
          ) : (
            <span className="italic text-muted">Not tied to one record</span>
          )}
          <span className="mt-0.5 block type-caption text-subtle">{humanLabel(log.module)}</span>
        </BandCell>

        <BandCell label="Came in from" icon={<Fingerprint className={cn('size-3', BAND_ICON[kind])} />} className="sm:pl-4">
          <span className="inline-flex items-center gap-1.5">{origin.icon}{origin.label}</span>
          {/* The row has carried an IP all along and nothing ever showed it — on an audit trail that
              is exactly the field a reviewer reaches for. */}
          <span className="mt-0.5 block truncate font-mono type-caption text-muted" title={log.ipAddress ?? undefined}>
            {log.ipAddress || 'IP not recorded'}
          </span>
        </BandCell>
      </div>

      {/* ── What the entry says, in its own words ── */}
      {log.message && (
        <Section title="What happened" icon={<MessageSquareText className="size-3.5" />}>
          <div className="break-words type-body leading-relaxed text-content">{prefix}</div>
          {fields.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="mr-0.5 type-overline text-subtle">Fields:</span>
              {fields.map((f) => <span key={f} className="inline-block rounded bg-amber-100 px-2 py-0.5 type-caption text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">{humanLabel(f)}</span>)}
            </div>
          )}
        </Section>
      )}

      {/* ── Genuine edit → a real before → after diff ── */}
      {diffMode && (
        <Section
          title="What changed" count={changed.length}
          icon={<Sparkles className="size-3.5 text-amber-500" />}
          className="border-amber-200 bg-amber-50/40 dark:border-amber-900/40 dark:bg-amber-900/10"
          titleClassName="text-amber-700 dark:text-amber-300"
        >
          <div className="overflow-x-auto rounded-control border border-amber-100 bg-surface dark:border-amber-900/30">
            <table className="w-full min-w-[520px] text-xs">
              <thead className="bg-amber-50/60 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                <tr>
                  <th className="px-3 py-2 text-left type-overline">Field</th>
                  <th className="px-3 py-2 text-left type-overline">Before</th>
                  <th className="w-6 px-1 py-2" />
                  <th className="px-3 py-2 text-left type-overline">After</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {changed.map((c) => (
                  <tr key={c.field} className="align-top">
                    <td className="whitespace-nowrap px-3 py-2 font-semibold text-content">{humanLabel(c.field)}</td>
                    <td className="max-w-[220px] px-3 py-2 text-muted line-through decoration-rose-300 decoration-1">{renderCell(c.old)}</td>
                    <td className="px-1 py-2 text-amber-400"><ArrowRight className="size-3.5" /></td>
                    <td className="max-w-[220px] px-2 py-2">
                      <div className="rounded-md bg-amber-50/50 px-2 py-1.5 ring-2 ring-amber-300 dark:bg-amber-900/20 dark:ring-amber-700">{renderCell(c.new)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* ── Create / delete / import (one-sided) → a labeled snapshot, not a misleading diff ── */}
      {!diffMode && snapshotEntries.length > 0 && (
        <Section
          title={snapshotTitle} count={snapshotEntries.length}
          icon={<FileText className="size-3.5" />}
          className={isDelete ? 'border-rose-200 bg-rose-50/30 dark:border-rose-900/40 dark:bg-rose-900/10' : undefined}
          titleClassName={isDelete ? 'text-rose-700 dark:text-rose-300' : undefined}
        >
          <div className="overflow-hidden rounded-control border border-line bg-surface">
            <table className="w-full text-xs">
              <tbody className="divide-y divide-line">
                {snapshotEntries.map(([k, v]) => (
                  <tr key={k} className="align-top">
                    <td className="w-[38%] whitespace-nowrap px-3 py-2 font-semibold text-content">{humanLabel(k)}</td>
                    <td className={cn('px-3 py-2', isDelete && 'text-muted')}>{renderCell(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* ── Technical view — the COMPLETE payload (housekeeping fields included) ── */}
      {(oldRaw || newRaw) && (
        <div className="mt-4">
          <button type="button" onClick={() => setShowRaw((v) => !v)} className="inline-flex items-center gap-1.5 type-caption text-muted hover:text-content">
            <Code className="size-3" /> {showRaw ? 'Hide full snapshot' : 'Show full snapshot'}
          </button>
          {showRaw && (
            <div className="mt-2 space-y-3">
              {oldRaw != null && (
                <div className="rounded-control border border-line">
                  <div className="px-3 py-2 type-overline text-muted">Before</div>
                  <div className="border-t border-line bg-surface-2/40 px-3 py-2"><LogValue value={oldRaw} /></div>
                </div>
              )}
              {newRaw != null && (
                <div className="rounded-control border border-line">
                  <div className="px-3 py-2 type-overline text-muted">After</div>
                  <div className="border-t border-line bg-surface-2/40 px-3 py-2"><LogValue value={newRaw} /></div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
